import { FC, HTMLAttributes, ReactNode } from "react";
import clsx from "clsx";
import Image from "next/image";
import { List, ListItem } from "@mantine/core";

import liIcon from "@/assets/images/icons/li.svg";

import TButton from "@/components/atoms/buttons/TButton";
import HtmlText from "@/sections/common/HtmlText";

import { ITextButton } from "@/components/atoms/buttons/types";
import { isEmpty } from "lodash";

// -----------------------------------------------------------------------------------

export type IListItemType = {
  name?: string;
};

export type ITextSectionContent = {
  heading?: string;
  description?: ReactNode;
  listItems?: IListItemType[][];
  buttons?: ITextButton[];
};

type ITextSectionProps = HTMLAttributes<HTMLDivElement> &
  ITextSectionContent & {
    innerContainerClass?: string;
    headingClass?: string;
    textSectionClass?: string;
    textWithImageRichTextStyle?: string;
    background?: "white" | "primary";
  };

const TextSection: FC<ITextSectionProps> = ({
  heading,
  description,
  listItems,
  buttons,
  className,
  headingClass,
  innerContainerClass,
  textSectionClass,
  textWithImageRichTextStyle,
  background,
  ...other
}) => {
  // Auto-detect multi-sentence headings (e.g. "Attacker-driven offense.
  // Evidence-driven defense. Intelligence-driven protection.") and render
  // each sentence on its own line, centered, with a black opacity gradient.
  // Single-sentence headings keep the original left-aligned treatment so we
  // don't surprise the dozens of other TextSection usages across the site.
  const sentences =
    heading && /\.\s+\S/.test(heading)
      ? heading.split(/\.\s+/).filter(Boolean)
      : null;
  const isMultiSentence = !!sentences && sentences.length > 1;

  return (
    <div className={clsx("py-5 lg:py-8 xl:py-[50px]", className)}>
      <div
        className={clsx(
          "dark:bg-black-900",
          background === "white" && "bg-white",
          background === "primary" &&
            "bg-primary-100 py-10 dark:py-0 lg:py-16 xl:py-[100px]",
          innerContainerClass,
        )}
        {...other}
      >
        <div
          className={clsx(
            "container flex h-fit flex-col justify-center self-center",
            isMultiSentence ? "items-center text-center" : "items-start",
            textSectionClass,
          )}
        >
          {/* Heading — canonical SectionHeader title scale
              (22px → 48px clamp). Multi-sentence variant adds the
              gradient text treatment + center alignment but uses the
              same clamp so both variants stay in the same type family. */}
          {heading && (
            <h2
              className={clsx(
                "font-bricolage-grotesque font-medium !leading-[1.1] dark:w-full dark:text-white",
                isMultiSentence
                  ? "mx-auto mb-4 max-w-4xl bg-gradient-to-b from-black-900 via-black-900/80 to-black-900/50 bg-clip-text text-[clamp(1.375rem,3vw+0.75rem,3rem)] text-transparent [text-wrap:balance] dark:from-white dark:via-white/90 dark:to-white/60 lg:mb-6"
                  : "mb-3 text-[clamp(1.375rem,3vw+0.75rem,3rem)] text-black-900 lg:mb-5 xl:mb-7",
                headingClass,
              )}
            >
              {isMultiSentence
                ? sentences!.map((sentence, i) => (
                    <span key={i} className="block">
                      {sentence}
                    </span>
                  ))
                : heading}
            </h2>
          )}

          {/* if description have paragraph */}
          <HtmlText
            className={clsx(
              textWithImageRichTextStyle,
              "dark:prose-p:md:!text-[19px] dark:prose-li:md:!text-[19px]",
            )}
          >
            {description}
          </HtmlText>

          {/* if description have list items */}
          {listItems && listItems.length > 0 && (
            <div className="mt-5 grid w-full grid-cols-1 lg:mt-7 lg:grid-cols-2 lg:gap-14 xl:gap-24">
              {listItems.map(
                (listItem, ind) =>
                  !isEmpty(listItem) && (
                    <List
                      key={ind}
                      spacing="xs"
                      size="sm"
                      center
                      className="pl-9 last:mb-0"
                      classNames={{
                        itemIcon: "mr-5 min-w-[22px] py-1.5",
                        itemWrapper: "items-start",
                      }}
                    >
                      {listItem.map((li, ind) => (
                        <ListItem
                          key={ind}
                          className="relative mb-4 text-base font-normal before:absolute before:-left-[34px] before:top-[1px] before:mt-[3px] before:h-4 before:w-4 before:rounded-full before:border-2 before:border-yellow-400 before:content-[''] after:absolute after:-left-[30px] after:top-[5px] after:mt-[3px] after:h-[8px] after:w-[8px] after:rounded-full after:bg-primary-900 after:text-h7 after:content-[''] dark:text-black-200 dark:before:border-white dark:after:bg-yellow-400 md:text-lg md:before:mt-[3px] md:after:mt-[3px] dark:md:text-[19px] lg:text-h7 before:lg:top-[4px] after:lg:top-[8px] after:lg:mt-[3px] after:lg:text-h6"
                        >
                          {li.name}
                        </ListItem>
                      ))}
                    </List>
                  ),
              )}
            </div>
          )}

          {/* if description have any buttons */}
          {buttons && buttons.length > 0 && (
            <div className="mt-5 flex flex-row flex-wrap gap-4 md:gap-8 lg:mt-7 lg:flex-col">
              {buttons.map((button) => (
                <TButton
                  key={button.text}
                  id={button.id}
                  variant={button.variant}
                  target={button.target}
                  radius={button.radius}
                  href={button.link}
                >
                  {button.text}
                </TButton>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TextSection;
